// Requotes & follow-up, Completed sales, Approvals.
import { useState } from 'react';
import { Link, useOutletContext } from 'react-router';
import { Banner, useApiClient, useQuery } from '@acc/web-shared';
import { missedKindLabel } from '@acc/domain';
import { PageHead, dateTime, shortDate, useAgency } from '../agency';
import { QuoteTable, type Opportunity } from './Quotes';

export function FollowUps() {
  const a = useAgency();
  const api = useApiClient();
  const { refreshCounts } = useOutletContext<{ refreshCounts: () => void }>();
  const q = useQuery<{ opportunities: Opportunity[] }>(a.path('/opportunities?view=followup'));
  const [busy, setBusy] = useState(false);
  async function reopenDue() {
    setBusy(true);
    try {
      const r = await api<{ reopened: number }>(a.path('/followups/reopen-due'), { method: 'POST' });
      a.notify('ok', r.reopened ? `${r.reopened} due follow-up(s) reopened for quoting.` : 'No follow-ups are due.');
      refreshCounts(); q.reload();
    } catch (err) { a.notify('bad', (err as Error).message); } finally { setBusy(false); }
  }
  return (
    <>
      <PageHead title="Requotes & follow-up" sub="Missed coverage and brokerage quotes, with their follow-up dates.">
        {a.manager && <button className="btn" type="button" disabled={busy} onClick={() => void reopenDue()}>{busy ? 'Checking…' : 'Reopen due follow-ups'}</button>}
      </PageHead>
      {q.error && <Banner tone="bad">{q.error.message}</Banner>}
      <div className="card">{q.loading ? <p className="muted">Loading…</p> : <QuoteTable rows={q.data?.opportunities ?? []} empty="Nothing needs follow-up." />}</div>
    </>
  );
}

interface Sale { opportunityId: string; customerName: string; type: string; origin: string; referredBy: string | null; coverage: 'PC' | 'Life'; ownerMembershipId: string | null; completedAt: string }

export function Sales() {
  const a = useAgency();
  const q = useQuery<{ sales: Sale[] }>(a.path('/sales'));
  return (
    <>
      <PageHead title="Completed sales" sub="Each completed coverage, credited to the owner at the time it was completed." />
      {q.error && <Banner tone="bad">{q.error.message}</Banner>}
      <div className="card">
        {q.data && q.data.sales.length === 0 && <div className="empty">No completed coverage yet.</div>}
        {q.data && q.data.sales.length > 0 && (
          <table className="data">
            <thead><tr><th>Customer</th><th>Coverage</th><th>Type</th><th>Credited to</th><th>Referred by</th><th>Completed</th></tr></thead>
            <tbody>
              {q.data.sales.map((s, i) => (
                <tr key={i}>
                  <td><Link to={`${a.base}/quotes/${s.opportunityId}`}>{s.customerName}</Link></td>
                  <td>{s.coverage === 'PC' ? 'P/C' : 'Life'}</td>
                  <td>{s.type}</td>
                  <td>{a.name(s.ownerMembershipId)}</td>
                  <td className="muted">{s.referredBy ?? '—'}</td>
                  <td className="muted">{shortDate(s.completedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

export function Approvals() {
  const a = useAgency();
  const api = useApiClient();
  const { refreshCounts } = useOutletContext<{ refreshCounts: () => void }>();
  const missed = useQuery<{ opportunities: Opportunity[] }>(a.manager ? a.path('/opportunities?view=approvals') : null);
  const followups = useQuery<{ opportunities: Opportunity[] }>(a.manager ? a.path('/opportunities?view=followup') : null);
  const reopen = (followups.data?.opportunities ?? []).filter((o) => o.reopenPending);
  const [busy, setBusy] = useState('');

  async function run(id: string, fn: () => Promise<unknown>, ok: string) {
    setBusy(id);
    try { await fn(); a.notify('ok', ok); refreshCounts(); missed.reload(); followups.reload(); }
    catch (err) { a.notify('bad', (err as Error).message); missed.reload(); followups.reload(); }
    finally { setBusy(''); }
  }

  if (!a.manager) return <Banner tone="warn">Approvals are for the agency owner and office manager.</Banner>;
  return (
    <>
      <PageHead title="Approvals" sub="Missed quotes awaiting review, and requests to reopen missed coverage early." />
      <section className="card" aria-labelledby="missed">
        <h2 id="missed">Missed quotes</h2>
        {missed.data && missed.data.opportunities.length === 0 && <div className="empty">No missed quotes waiting.</div>}
        {(missed.data?.opportunities ?? []).length > 0 && (
          <table className="data">
            <thead><tr><th>Customer</th><th>Owner</th><th>Updated</th><th /></tr></thead>
            <tbody>
              {missed.data!.opportunities.map((o) => (
                <tr key={o.opportunityId}>
                  <td><Link to={`${a.base}/quotes/${o.opportunityId}`}>{o.customerName}</Link><div className="muted" style={{ fontSize: 13 }}>{o.type}</div></td>
                  <td>{a.name(o.ownerMembershipId)}</td>
                  <td className="muted">{dateTime(o.updatedAt)}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button className="btn small primary" disabled={busy === o.opportunityId} onClick={() => void run(o.opportunityId, () => api(a.path(`/opportunities/${o.opportunityId}/approval`), { method: 'POST', body: { decision: 'Approved', version: o.version } }), `${o.customerName}: approved for follow-up.`)}>Approve</button>{' '}
                    <button className="btn small danger" disabled={busy === o.opportunityId} onClick={() => void run(o.opportunityId, () => api(a.path(`/opportunities/${o.opportunityId}/approval`), { method: 'POST', body: { decision: 'Rejected', version: o.version } }), `${o.customerName}: rejected.`)}>Reject</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
      <section className="card" aria-labelledby="reopen">
        <h2 id="reopen">Early reopening requests</h2>
        {followups.data && reopen.length === 0 && <div className="empty">No reopening requests.</div>}
        {reopen.length > 0 && (
          <table className="data">
            <thead><tr><th>Customer</th><th>Missed</th><th>Owner</th><th /></tr></thead>
            <tbody>
              {reopen.map((o) => (
                <tr key={o.opportunityId}>
                  <td><Link to={`${a.base}/quotes/${o.opportunityId}`}>{o.customerName}</Link></td>
                  <td>{missedKindLabel(o)}</td>
                  <td>{a.name(o.ownerMembershipId)}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button className="btn small primary" disabled={busy === o.opportunityId} onClick={() => void run(o.opportunityId, () => api(a.path(`/opportunities/${o.opportunityId}/reopen-request/decision`), { method: 'POST', body: { approved: true } }), `${o.customerName}: reopened for quoting.`)}>Approve</button>{' '}
                    <button className="btn small" disabled={busy === o.opportunityId} onClick={() => void run(o.opportunityId, () => api(a.path(`/opportunities/${o.opportunityId}/reopen-request/decision`), { method: 'POST', body: { approved: false } }), `${o.customerName}: request declined.`)}>Decline</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}
